import { AskRequest } from '@hone-automation/common/lib/AskRequests';
import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  FilterProps,
  filterRequestsBySearch,
  filterRequestsByStatus,
  splitRequestsByPriority,
} from './utils/filterAndSearchUtils';
import { selectAskRequestElement } from './utils/selectElement';
import { AskRequestStatus, QueueView, QueueViewCountsResponse } from '@hone-automation/common';
import { computeLocalViewCounts, filterRequestsByView } from './utils/viewCategorization';
import { getDateGroupKey, getGroupLabel } from '../KSQ2/Panels/BrowserPanel/utils/dateGrouping';
import { useGetQueueRequestsQuery } from 'hooks/queue/useGetQueueRequestsQuery';
import { useGetQueueRequestsCountQuery } from 'hooks/queue/useGetQueueRequestsCountQuery';
import { useFetchQueueRequestById } from 'hooks/queue/useFetchQueueRequestById';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { useQueryState } from 'hooks/useQueryState';

// Re-export QueueView for components that were using FilterView
export type { QueueView };

/**
 * @deprecated Use QueueView from @hone-automation/common instead
 * Kept for backwards compatibility during migration
 */
export type FilterView = QueueView;

/**
 * Default filter settings for request handling
 */
const defaultFilters: FilterProps = {
  status: 'Open',
  search: '',
  sort: 'oldest',
  groupBy: 'date',
};

// Define the initial state as a function that reads from URL search params
const initialState = (props: { meId?: string; requests?: AskRequest[]; disableAutoSelect?: boolean }) => {
  let status = defaultFilters.status;
  let search = defaultFilters.search;
  let sort = defaultFilters.sort;
  let groupBy = defaultFilters.groupBy;
  let viewFromUrl: QueueView | undefined = undefined;

  let parentIdFromUrl: string | null = null;

  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    status = (searchParams.get('status') || defaultFilters.status) as FilterProps['status'];
    search = searchParams.get('search') || defaultFilters.search;
    sort = (searchParams.get('sort') || defaultFilters.sort) as FilterProps['sort'];
    groupBy = (searchParams.get('groupBy') || defaultFilters.groupBy) as FilterProps['groupBy'];
    // If requestId is present without parentId, the deep link flow will determine the view
    // via the backend — don't pre-set activeView from the URL's view param in that case,
    // because it would create a misleading initial state that the deep link will immediately override.
    const requestIdParam = searchParams.get('requestId');
    const rawParentId = searchParams.get('parentId');
    const isRequestIdOnlyDeepLink = !!(requestIdParam && requestIdParam.trim()) && !(rawParentId && rawParentId.trim());

    const viewParam = searchParams.get('view');
    if (
      viewParam &&
      ['Blockers', 'Me', 'My Team', 'Client', 'Completed', 'Groups'].includes(viewParam) &&
      !isRequestIdOnlyDeepLink
    ) {
      viewFromUrl = viewParam as QueueView;
    }
    // If parentId is in URL (Groups deeplink), activate Groups view
    if (rawParentId && rawParentId.trim()) {
      parentIdFromUrl = rawParentId.trim();
      if (!viewFromUrl) {
        viewFromUrl = 'Groups';
      }
    }
  }

  const initialFilters = {
    status,
    search,
    sort,
    groupBy,
  };

  // 1. Filter raw requests by SEARCH term only (to match reducer logic)
  const searchedRequests =
    props.requests && props.meId
      ? filterRequestsBySearch(props.requests, initialFilters.search || '')
      : ([] as AskRequest[]);

  // 2. Split the filtered requests by priority
  const initialPriorityGroups = splitRequestsByPriority(searchedRequests);

  // 3. Filter the NORMAL priority group by STATUS (blockers are NOT filtered by status)
  const initialFilteredNormalRequests = filterRequestsByStatus(initialPriorityGroups.Normal, initialFilters.status);

  return {
    requests: props.requests || [], // Raw, unfiltered requests from props
    filteredNormalRequests: initialFilteredNormalRequests, // Normal requests after applying status filter
    priorityGroups: initialPriorityGroups, // Blockers include ALL statuses, Normal is pre-status-filter
    selectedItem:
      !props.disableAutoSelect && searchedRequests.length > 0
        ? selectAskRequestElement(
            [...initialPriorityGroups.Blocker, ...initialFilteredNormalRequests],
            initialFilters.sort
          )
        : null,
    meId: props.meId || '',
    filters: initialFilters as FilterProps,
    collapsedGroups: {} as Record<string, boolean>,
    newRequestAdded: null as AskRequest | null,
    // KSQ2 FilterPanel view - undefined means load all requests
    activeView: viewFromUrl as QueueView | undefined,
    // Active group ID when viewing groups filter — initialized from parentId URL param for deeplinks
    activeGroupId: parentIdFromUrl as string | null,
    // Highlighted filter for drop animation (auto-clears after animation)
    highlightedFilter: null as QueueView | null,
    // IDs of requests currently visible in BrowserPanel (after all filters + search)
    // Used by Dashboard to determine if selectedItem should be shown
    visibleRequestIds: new Set<string>(),
    // Loading state for data fetching
    isLoading: false,
  };
};

type SetRequestsAction = { type: 'SET_REQUESTS'; payload: AskRequest[] };
type SetSelectedItemAction = { type: 'SET_SELECTED_ITEM'; payload: AskRequest | null };
type SetMeIdAction = { type: 'SET_ME_ID'; payload: string };
type SetUpdatedFiltersAction = { type: 'SET_FILTERS'; payload: FilterProps };
type SetStatusFilterAction = { type: 'SET_STATUS_FILTER'; payload: FilterProps['status'] };
type SetGroupCollapsedAction = { type: 'SET_GROUP_COLLAPSED'; payload: { groupId: string; isCollapsed: boolean } };
type AddNewRequestAction = { type: 'ADD_NEW_REQUEST'; payload: { request: AskRequest; status: AskRequestStatus } };
type UpdateRequestAction = { type: 'UPDATE_REQUEST'; payload: { request: AskRequest; status: AskRequestStatus } };
type SetNewRequestAddedAction = { type: 'SET_NEW_REQUEST_ADDED'; payload: AskRequest | null };
type SetActiveViewAction = { type: 'SET_ACTIVE_VIEW'; payload: QueueView | undefined };
type SetActiveGroupIdAction = { type: 'SET_ACTIVE_GROUP_ID'; payload: string | null };
type HighlightFilterAction = { type: 'HIGHLIGHT_FILTER'; payload: QueueView | null };
type SetVisibleRequestIdsAction = { type: 'SET_VISIBLE_REQUEST_IDS'; payload: Set<string> };
type UpdateRequestParentIdAction = {
  type: 'UPDATE_REQUEST_PARENT_ID';
  payload: { requestId: string; parentId: string | null };
};
type SetLoadingAction = { type: 'SET_LOADING'; payload: boolean };
type Action =
  | SetRequestsAction
  | SetSelectedItemAction
  | SetMeIdAction
  | SetUpdatedFiltersAction
  | SetStatusFilterAction
  | SetGroupCollapsedAction
  | AddNewRequestAction
  | UpdateRequestAction
  | SetNewRequestAddedAction
  | SetActiveViewAction
  | SetActiveGroupIdAction
  | HighlightFilterAction
  | SetVisibleRequestIdsAction
  | UpdateRequestParentIdAction
  | SetLoadingAction;

const queueReducer = (state: ReturnType<typeof initialState>, action: Action) => {
  switch (action.type) {
    case 'SET_REQUESTS': {
      const newRequests = action.payload;
      // 1. Filter raw requests by SEARCH term only
      const searchedRequests = filterRequestsBySearch(newRequests, state.filters.search || '');
      // 2. Split the filtered requests by priority
      const newPriorityGroups = splitRequestsByPriority(searchedRequests);
      // 3. Filter the NORMAL priority group by STATUS
      const newFilteredNormalRequests = filterRequestsByStatus(newPriorityGroups.Normal, state.filters.status);

      return {
        ...state,
        requests: newRequests,
        filteredNormalRequests: newFilteredNormalRequests,
        priorityGroups: newPriorityGroups,
        selectedItem: state.selectedItem, // Keep selection if requests refresh
      };
    }
    case 'SET_SELECTED_ITEM':
      return {
        ...state,
        selectedItem: action.payload,
      };
    case 'SET_ME_ID':
      return { ...state, meId: action.payload };
    case 'SET_FILTERS': {
      const newFilters = { ...state.filters, ...action.payload };

      // 1. Filter raw requests by the NEW SEARCH term only
      const searchedRequests = filterRequestsBySearch(state.requests, newFilters.search || '');

      // 2. Split the newly filtered requests by priority
      const newPriorityGroups = splitRequestsByPriority(searchedRequests);

      // 3. Filter the NORMAL priority group by the NEW STATUS
      const newFilteredNormalRequests = filterRequestsByStatus(newPriorityGroups.Normal, newFilters.status);

      // 4. Auto-select first item from the filtered list
      const allFilteredRequests = [...(newPriorityGroups.Blocker || []), ...newFilteredNormalRequests];
      const selectedItem = selectAskRequestElement(allFilteredRequests, newFilters.sort);

      return {
        ...state,
        filters: newFilters,
        filteredNormalRequests: newFilteredNormalRequests,
        priorityGroups: newPriorityGroups,
        selectedItem,
      };
    }
    case 'SET_STATUS_FILTER': {
      const newFilters = { ...state.filters, status: action.payload };
      // 1. Filter raw requests by the NEW SEARCH term only
      const searchedRequests = filterRequestsBySearch(state.requests, newFilters.search || '');
      // 2. Split the newly filtered requests by priority
      const newPriorityGroups = splitRequestsByPriority(searchedRequests);
      // 3. Filter the NORMAL priority group by the NEW STATUS
      const newFilteredNormalRequests = filterRequestsByStatus(newPriorityGroups.Normal, newFilters.status);

      // 4. Auto-select first item from the filtered list
      const allFilteredRequests = [...(newPriorityGroups.Blocker || []), ...newFilteredNormalRequests];
      const selectedItem = selectAskRequestElement(allFilteredRequests, newFilters.sort);

      return {
        ...state,
        filters: newFilters,
        filteredNormalRequests: newFilteredNormalRequests,
        priorityGroups: newPriorityGroups,
        selectedItem,
      };
    }
    case 'SET_NEW_REQUEST_ADDED':
      return { ...state, newRequestAdded: action.payload };

    case 'SET_GROUP_COLLAPSED': {
      const { groupId, isCollapsed } = action.payload;
      return {
        ...state,
        collapsedGroups: {
          ...state.collapsedGroups,
          [groupId]: isCollapsed,
        },
      };
    }
    case 'ADD_NEW_REQUEST': {
      const { request, status } = action.payload;
      const newRequests = [...state.requests, request];

      // Apply the same filtering logic as SET_FILTERS/SET_REQUESTS
      // 1. Filter by the current search term
      const searchedRequests = filterRequestsBySearch(newRequests, state.filters.search || '');
      // 2. Split the search-filtered requests by priority
      const newPriorityGroups = splitRequestsByPriority(searchedRequests);
      // 3. Filter the NORMAL priority group by the new request's status (or current filter status?)
      //    Let's use the current filter status for consistency, adding a new request shouldn't change the *view*
      const newFilteredNormalRequests = filterRequestsByStatus(newPriorityGroups.Normal, state.filters.status);

      // Determine which group the new request belongs to for potential UI updates (e.g., scrolling)
      const date = new Date(request.createdAt);
      const now = new Date();
      const groupKey = getDateGroupKey(date, now);
      const groupId = getGroupLabel(groupKey, now);

      // Update collapsedGroups to ensure the group containing the new request is expanded
      const updatedCollapsedGroups = { ...state.collapsedGroups };
      if (groupId) {
        updatedCollapsedGroups[groupId] = false;
      }

      return {
        ...state,
        requests: newRequests,
        filters: state.filters, // Keep existing filters
        filteredNormalRequests: newFilteredNormalRequests,
        priorityGroups: newPriorityGroups,
        selectedItem: request, // Select the newly added request
        collapsedGroups: updatedCollapsedGroups,
        newRequestAdded: request, // Set the newly added request
      };
    }
    case 'UPDATE_REQUEST': {
      const { request } = action.payload;
      // Update the request in the list
      const updatedRequests = state.requests.map(req => (req.id === request.id ? request : req));

      // Keep current filters unchanged - don't change tab
      const searchedRequests = filterRequestsBySearch(updatedRequests, state.filters.search || '');
      const newPriorityGroups = splitRequestsByPriority(searchedRequests);
      const newFilteredNormalRequests = filterRequestsByStatus(newPriorityGroups.Normal, state.filters.status);

      // Don't change selectedItem here - let the calling component handle selection
      return {
        ...state,
        requests: updatedRequests,
        filteredNormalRequests: newFilteredNormalRequests,
        priorityGroups: newPriorityGroups,
        // Keep existing filters and selectedItem unchanged
      };
    }
    case 'SET_ACTIVE_VIEW':
      return {
        ...state,
        activeView: action.payload,
        // Keep activeGroupId when viewing group children, clear it when switching to other views
        activeGroupId: action.payload === 'Groups' ? state.activeGroupId : null,
      };
    case 'SET_ACTIVE_GROUP_ID':
      return {
        ...state,
        activeGroupId: action.payload,
      };
    case 'HIGHLIGHT_FILTER':
      return {
        ...state,
        highlightedFilter: action.payload,
      };
    case 'SET_VISIBLE_REQUEST_IDS':
      return {
        ...state,
        visibleRequestIds: action.payload,
      };
    case 'UPDATE_REQUEST_PARENT_ID': {
      const { requestId, parentId } = action.payload;
      // Update the request's parentId in the requests array
      const updatedRequests = state.requests.map(req =>
        req.id === requestId ? { ...req, parentId: parentId ?? undefined } : req
      );

      // Re-apply filtering logic
      const searchedRequests = filterRequestsBySearch(updatedRequests, state.filters.search || '');
      const newPriorityGroups = splitRequestsByPriority(searchedRequests);
      const newFilteredNormalRequests = filterRequestsByStatus(newPriorityGroups.Normal, state.filters.status);

      // Also update selectedItem if it's the affected request
      const updatedSelectedItem =
        state.selectedItem?.id === requestId
          ? { ...state.selectedItem, parentId: parentId ?? undefined }
          : state.selectedItem;

      return {
        ...state,
        requests: updatedRequests,
        filteredNormalRequests: newFilteredNormalRequests,
        priorityGroups: newPriorityGroups,
        selectedItem: updatedSelectedItem,
      };
    }
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

/** Default counts when data is not yet loaded */
const defaultViewCounts: QueueViewCountsResponse = {
  Blockers: 0,
  Me: 0,
  'My Team': 0,
  Client: 0,
  Completed: 0,
  Groups: [],
};

/** Query data exposed through context */
interface QueueQueryData {
  /** Server-filtered requests based on activeView */
  queryRequests: AskRequest[];
  /** Whether the query is currently loading */
  isQueryLoading: boolean;
  /** The view returned by the server (useful when loading by requestId) */
  queryView: QueueView | undefined;
  /** Counts per view for FilterPanel badges */
  viewCounts: QueueViewCountsResponse;
}

const QueueContext = createContext<{
  state: ReturnType<typeof initialState>;
  dispatch: React.Dispatch<Action>;
  queryData: QueueQueryData;
  /** True while a requestId or parentId deeplink is being resolved by the server */
  isDeepLinkPending: boolean;
}>({
  state: initialState({}),
  dispatch: () => {},
  queryData: { queryRequests: [], isQueryLoading: false, queryView: undefined, viewCounts: defaultViewCounts },
  isDeepLinkPending: false,
});

/**
 * Hook to access the Queue context data and actions
 * @returns {Object} Queue context value containing state, dispatch, and queryData
 */
export const useQueueContext = () => useContext(QueueContext);

/**
 * Provider component for Queue context
 * Manages state for requests filtering, selection, and synchronization with URL parameters
 *
 * @param {Object} props - The provider props
 * @param {string} [props.meId] - Current user ID for filtering requests
 * @param {AskRequest[]} [props.requests] - Array of request data
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 * @returns {JSX.Element} The provider component
 */
export const QueueContextProvider = ({
  meId,
  requests,
  children,
  syncToUrl = true,
}: {
  meId?: string;
  requests?: AskRequest[];
  children: React.ReactNode;
  /** When false, use prop-provided requests locally instead of fetching from server. Disables URL sync. */
  syncToUrl?: boolean;
}) => {
  const [state, dispatch] = useReducer(queueReducer, { meId, requests, disableAutoSelect: !syncToUrl }, initialState);
  const currentLocationId = useLocationsStore(s => s.currentLocationId);
  const navigate = useNavigate();
  const location = useLocation();
  const [requestIdFromUrl] = useQueryState('requestId');
  const [parentIdFromUrl] = useQueryState('parentId');

  // Parse requestId and parentId from URL
  const requestIdString =
    typeof requestIdFromUrl === 'string' && requestIdFromUrl.trim() ? requestIdFromUrl : undefined;
  const parentIdString = typeof parentIdFromUrl === 'string' && parentIdFromUrl.trim() ? parentIdFromUrl : undefined;

  // Track requestId deep link state - starts true if URL has requestId WITHOUT parentId.
  // When parentId is also present, parentId drives the view (Groups) and requestId is
  // used only for item selection within that group — not for the backend view-lookup flow.
  const initialRequestIdRef = useRef(requestIdString);
  // isDeepLinkPending only when requestId is present WITHOUT parentId.
  // When parentId is also in URL, parentId drives the view (Groups) and requestId is used only
  // for item selection within that group via the parentId deeplink flow.
  const [isDeepLinkPending, setIsDeepLinkPending] = useState(!!requestIdString && !parentIdString && syncToUrl);
  const queryClient = useQueryClient();
  const fetchQueueRequestById = useFetchQueueRequestById();

  // Track the resolved view from deep link to prevent race conditions
  // This ensures we always have a valid view when exiting deep link mode
  const resolvedDeepLinkViewRef = useRef<QueueView | undefined>(undefined);

  // Track parentId deep link state - starts true if URL has parentId, becomes false once resolved
  const initialParentIdRef = useRef(parentIdString);
  const [isParentIdDeepLinkPending, setIsParentIdDeepLinkPending] = useState(syncToUrl && !!parentIdString);

  // Combined deep link pending: true while EITHER requestId or parentId deeplink is unresolved.
  // Exposed through context so child components (e.g. useDefaultFilter) can reliably skip
  // auto-selection without re-parsing the URL independently.
  const isAnyDeepLinkPending = isDeepLinkPending || isParentIdDeepLinkPending;

  // Query parameters:
  // - Deep link pending: send requestId to backend (backend determines view)
  // - After resolved: send view (from URL, state, OR resolved deep link ref as fallback)
  // - If activeGroupId is set: pass it as parentId to fetch group children
  // - NEVER call without either view or requestId (guard is also in the hook)
  const currentView = state.activeView ?? resolvedDeepLinkViewRef.current;
  const parentIdForQuery = state.activeGroupId ?? undefined;
  // When parentId is set, force view to 'Groups' — URL may still have the previous view (e.g. 'Me')
  const activeViewForQuery = isDeepLinkPending ? undefined : parentIdForQuery ? ('Groups' as QueueView) : currentView;
  const requestIdForQuery = isDeepLinkPending ? initialRequestIdRef.current : undefined;

  // Fetch queue request counts for FilterPanel badges (fetch early so we can use it for transition logic)
  const { data: countResponse, isLoading: countsLoading } = useGetQueueRequestsCountQuery({
    locationId: syncToUrl ? currentLocationId : undefined,
  });

  // Track view transitions to show loading skeleton
  const displayedViewRef = useRef<QueueView | undefined>(activeViewForQuery);
  const [isViewTransitioning, setIsViewTransitioning] = useState(false);

  // Detect view changes - only show transition loading if the target view has items
  useEffect(() => {
    if (!syncToUrl) return;
    if (activeViewForQuery !== displayedViewRef.current) {
      // Only show transition if the target view has items to load
      const rawCount = activeViewForQuery ? countResponse?.[activeViewForQuery] ?? 0 : 0;
      const targetViewCount = Array.isArray(rawCount) ? rawCount.length : rawCount;
      if (targetViewCount > 0) {
        setIsViewTransitioning(true);
      }
    }
  }, [activeViewForQuery, countResponse, syncToUrl]);

  // Fetch queue requests - when requestId is provided (deep link only), backend returns the view + requests
  // When parentId is provided, backend should return children of that group
  const {
    data: queryResponse,
    isLoading: requestsLoading,
    isFetching: requestsFetching,
  } = useGetQueueRequestsQuery({
    locationId: syncToUrl ? currentLocationId : undefined,
    view: syncToUrl ? activeViewForQuery : undefined,
    requestId: syncToUrl ? requestIdForQuery : undefined,
    parentId: syncToUrl ? parentIdForQuery : undefined,
  });

  // Local mode: sync prop requests into reducer when they change
  const prevLocalRequestsRef = useRef(requests);
  useEffect(() => {
    if (syncToUrl) return;
    if (requests && requests !== prevLocalRequestsRef.current) {
      prevLocalRequestsRef.current = requests;
      dispatch({ type: 'SET_REQUESTS', payload: requests });
    }
  }, [requests, syncToUrl]);

  // Local mode: set default view to 'My Team' on mount
  useEffect(() => {
    if (syncToUrl) return;
    if (!state.activeView) {
      dispatch({ type: 'SET_ACTIVE_VIEW', payload: 'My Team' });
    }
  }, [syncToUrl]);

  // Update displayed view reference when query completes - reset transition state
  useEffect(() => {
    if (!syncToUrl) return;
    if (queryResponse?.view === activeViewForQuery && !requestsFetching) {
      displayedViewRef.current = activeViewForQuery;
      setIsViewTransitioning(false);
    }
  }, [queryResponse?.view, activeViewForQuery, requestsFetching, syncToUrl]);

  // Ref to track if deep link has been applied (prevents re-running)
  const hasAppliedDeepLink = useRef(false);

  // When loading by requestId (deep link), sync the server-returned view to state and select the request
  // This runs once when the deep link query returns, then exits deep link mode
  useEffect(() => {
    if (!syncToUrl) return;
    if (!isDeepLinkPending) return;
    if (!initialRequestIdRef.current) return;
    if (!queryResponse?.view || !queryResponse?.requests) return;
    if (hasAppliedDeepLink.current || requestsFetching) return;

    hasAppliedDeepLink.current = true;

    // Find the target request from the URL
    const targetRequest = queryResponse.requests.find(r => r.id === initialRequestIdRef.current);

    if (targetRequest?.parentId) {
      // Request belongs to a group — preselect the group in FilterPanel and the request in browser panel
      resolvedDeepLinkViewRef.current = 'Groups';
      dispatch({ type: 'SET_ACTIVE_GROUP_ID', payload: targetRequest.parentId });
      dispatch({ type: 'SET_ACTIVE_VIEW', payload: 'Groups' });
      dispatch({ type: 'SET_SELECTED_ITEM', payload: targetRequest });
    } else {
      // Regular request — use the backend-returned view
      resolvedDeepLinkViewRef.current = queryResponse.view;

      // Pre-populate the view-based query cache with the data from the deep link request
      // This prevents a redundant request when switching from requestId to view-based querying
      // Query key format: ['queueRequests', view, requestId, locationId, parentId]
      queryClient.setQueryData(['queueRequests', queryResponse.view, undefined, currentLocationId, undefined], {
        view: queryResponse.view,
        requests: queryResponse.requests,
      });

      // Set the view returned by backend (this is where the request actually lives)
      dispatch({ type: 'SET_ACTIVE_VIEW', payload: queryResponse.view });

      if (targetRequest) {
        dispatch({ type: 'SET_SELECTED_ITEM', payload: targetRequest });
      }
    }

    // Exit deep link mode - subsequent queries will use view instead of requestId.
    setIsDeepLinkPending(false);
  }, [
    isDeepLinkPending,
    queryResponse?.view,
    queryResponse?.requests,
    requestsFetching,
    dispatch,
    queryClient,
    currentLocationId,
    syncToUrl,
  ]);

  // parentId + requestId deeplink: preselect the group and select the specific child request.
  useEffect(() => {
    if (!syncToUrl) return;
    if (!isParentIdDeepLinkPending) return;
    if (!initialParentIdRef.current || !initialRequestIdRef.current) return;
    if (queryResponse?.requests === undefined || requestsFetching) return;

    const targetRequest = queryResponse.requests.find(r => r.id === initialRequestIdRef.current);
    if (targetRequest) {
      dispatch({ type: 'SET_SELECTED_ITEM', payload: targetRequest });
    }
    setIsParentIdDeepLinkPending(false);
  }, [syncToUrl, isParentIdDeepLinkPending, queryResponse?.requests, requestsFetching, dispatch]);

  // parentId-only deeplink: fetch and show the group parent (no child pre-selected).
  // Resolve pending AFTER the fetch so isQueryLoading stays true until the group is ready.
  useEffect(() => {
    if (!syncToUrl) return;
    if (!isParentIdDeepLinkPending) return;
    if (!initialParentIdRef.current || initialRequestIdRef.current) return;
    if (queryResponse?.requests === undefined || requestsFetching) return;

    const groupId = initialParentIdRef.current;
    fetchQueueRequestById(groupId)
      .then(group => {
        dispatch({ type: 'SET_SELECTED_ITEM', payload: group });
        setIsParentIdDeepLinkPending(false);
      })
      .catch(() => {
        setIsParentIdDeepLinkPending(false);
      });
  }, [
    syncToUrl,
    isParentIdDeepLinkPending,
    queryResponse?.requests,
    requestsFetching,
    dispatch,
    fetchQueueRequestById,
  ]);

  // Query data exposed through context
  // In local mode (syncToUrl=false), use prop-provided requests directly
  // In server mode, derive loading state from query status
  const isQueryLoading = syncToUrl
    ? requestsLoading ||
      countsLoading ||
      isViewTransitioning ||
      // Keep loading while a requestId deeplink is pending so BrowserPanel's auto-select
      // doesn't race with QueueContext's deep link effect and override the correct selection.
      isDeepLinkPending ||
      // Keep loading until the group parent is fetched (parentId-only deeplink).
      // Prevents BrowserPanel from auto-selecting the first child before the group is ready.
      isParentIdDeepLinkPending ||
      (requestsFetching && activeViewForQuery !== queryResponse?.view) ||
      (activeViewForQuery !== undefined && activeViewForQuery !== queryResponse?.view)
    : false;

  // Local mode: compute view counts and filter requests by activeView
  const localQueryData = useMemo<QueueQueryData>(() => {
    const viewCounts = state.meId ? computeLocalViewCounts(state.requests, state.meId) : defaultViewCounts;
    const filtered =
      state.activeView && state.meId
        ? filterRequestsByView(state.requests, state.activeView, state.meId)
        : state.requests;
    return {
      queryRequests: filtered,
      isQueryLoading: false,
      queryView: state.activeView,
      viewCounts,
    };
  }, [state.requests, state.activeView, state.meId]);

  const queryData: QueueQueryData = syncToUrl
    ? {
        queryRequests: queryResponse?.requests ?? [],
        isQueryLoading,
        queryView: queryResponse?.view,
        viewCounts: countResponse ?? defaultViewCounts,
      }
    : localQueryData;

  // Select the first item after data loads, except when a deeplink is in flight or we're on Groups.
  useEffect(() => {
    if (!syncToUrl) return;

    const pendingDeepLink =
      isAnyDeepLinkPending ||
      state.activeView === 'Groups' ||
      state.selectedItem ||
      requestsLoading ||
      requestsFetching;
    if (pendingDeepLink) {
      return;
    }

    const list = queryResponse?.requests;
    if (!list?.length) return;

    const firstItem = selectAskRequestElement(list, state.filters.sort);
    if (firstItem) {
      dispatch({ type: 'SET_SELECTED_ITEM', payload: firstItem });
    }
  }, [
    syncToUrl,
    isAnyDeepLinkPending,
    state.activeView,
    state.selectedItem,
    queryResponse?.requests,
    state.filters.sort,
    requestsLoading,
    requestsFetching,
    dispatch,
  ]);

  // Sync view/filter parameters to URL.
  // Skip while any deeplink is pending: the deeplink effect(s) own the URL during resolution
  // and the filter params will be synced once the resolved view/state is known.
  useEffect(() => {
    if (!syncToUrl) return;
    if (isAnyDeepLinkPending) return;
    if (typeof window === 'undefined') return;

    const newParams = new URLSearchParams(window.location.search);

    // Remove legacy status param from URL (we now use view)
    newParams.delete('status');

    // Sync non-status filter parameters
    (Object.entries(state.filters) as [keyof FilterProps, string][]).forEach(([key, value]) => {
      if (key !== 'status') {
        newParams.set(key, value);
      }
    });

    // Sync view/parentId parameter.
    // Groups view is represented as parentId= in the URL; all other views use view=.
    // Preserve requestId when a specific child inside the active group is selected so refreshes
    // can reopen that exact request; otherwise clear requestId to keep the group context clean.
    const selectedRequestInActiveGroup =
      state.activeView === 'Groups' &&
      state.activeGroupId &&
      state.selectedItem &&
      state.selectedItem.type !== 'Group' &&
      state.selectedItem.parentId === state.activeGroupId;

    if (state.activeView === 'Groups' && state.activeGroupId) {
      newParams.delete('view');
      newParams.set('parentId', state.activeGroupId);

      if (selectedRequestInActiveGroup) {
        newParams.set('requestId', state.selectedItem!.id);
      } else {
        newParams.delete('requestId');
      }
    } else if (state.activeView && state.activeView !== 'Groups') {
      newParams.set('view', state.activeView);
      newParams.delete('parentId');
    } else if (!state.activeView) {
      newParams.delete('view');
      newParams.delete('parentId');
    }
    const newUrlParams = newParams.toString();
    if (location.search.substring(1) !== newUrlParams) {
      navigate({ search: newUrlParams }, { replace: true });
    }
  }, [
    state.filters,
    state.activeView,
    state.activeGroupId,
    location.pathname,
    location.search,
    navigate,
    syncToUrl,
    isAnyDeepLinkPending,
  ]);

  return (
    <QueueContext.Provider value={{ state, dispatch, queryData, isDeepLinkPending: isAnyDeepLinkPending }}>
      {children}
    </QueueContext.Provider>
  );
};
